import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { auth } from '../../firebase/config';
import { getApiBaseUrl } from '../../utils/apiConfig';
import './IrDashboard.css';

const RANGES = [
  { key: '7d', label: '1주' },
  { key: '30d', label: '1달' },
  { key: '365d', label: '1년' },
  { key: 'all', label: '전체' },
  { key: 'custom', label: '직접 지정' },
];

const fmtWon = (n) => n == null ? '—' : '₩' + Math.round(n || 0).toLocaleString('ko-KR');
const fmtNum = (n) => n == null ? '—' : Math.round(n || 0).toLocaleString('ko-KR');
const fmtPct = (n) => n == null ? '—' : `${n}%`;
const fmtSignedWon = (n) => {
  if (n == null) return '—';
  const value = Math.round(n || 0);
  const abs = Math.abs(value).toLocaleString('ko-KR');
  if (value > 0) return `+₩${abs}`;
  if (value < 0) return `-₩${abs}`;
  return '₩0';
};
const fmtPctSigned = (n) => {
  if (n == null) return '—';
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(1)}%`;
};
const fmtRatio = (numerator, denominator, suffix = '명') => {
  if (numerator == null || denominator == null) return '—';
  return `${fmtNum(numerator)}${suffix} / ${fmtNum(denominator)}${suffix}`;
};
const EDTECH_SAAS_BENCHMARKS = [
  {
    metric: 'Opt-in 무료체험 → 유료 전환',
    benchmark: 'Good 4~6%, Great 10~15%',
    source: 'ChartMogul x ProductLed 2026 B2B software 200개 분석',
    url: 'https://chartmogul.com/reports/saas-conversion-report/',
  },
  {
    metric: 'EdTech Trial → Paid',
    benchmark: '중앙값 12~20%, 상위권 25~40%',
    source: 'Lifecycle Architect EdTech benchmark 2026',
    url: 'https://lifecyclearchitect.com/benchmarks/edtech-trial-to-paid-rate-benchmarks/',
  },
  {
    metric: 'EdTech 연간 고객 이탈',
    benchmark: '약 22% 수준까지 상승 보고',
    source: '2026 SaaS Benchmarks Report, EdTech vertical',
    url: 'https://www.saasrise.com/blog/saas-benchmark-report-2026',
  },
  {
    metric: 'EdTech NRR',
    benchmark: '중앙값 약 98~100%, 상위권 110%+',
    source: '2026 SaaS Benchmarks Report',
    url: 'https://www.saasrise.com/blog/saas-benchmark-report-2026',
  },
  {
    metric: 'B2B SaaS DAU/MAU',
    benchmark: '10~25% 건강 범위, 업무형 SaaS는 WAU/MAU 우선',
    source: 'B2B SaaS product metrics benchmark',
    url: 'https://www.basedash.com/startup-metrics/product-metrics',
  },
];

const ga4BenchmarkText = {
  activeUsers: '방향: MoM 증가가 핵심 · GA4는 비로그인 포함',
  sessions: '채널/광고 확장 시 증가 · 전환 품질은 별도 확인',
  pageViews: '콘텐츠/랜딩 관심도 보조 지표',
  engagementRate: '참여율은 높을수록 좋음 · 광고 유입이 많으면 낮아질 수 있음',
};

const monthKeyFromDate = (dateValue) => (dateValue || '').slice(0, 7);

const filterRowsByRange = (rows, range, startDate, endDate) => {
  if (!rows?.length) return [];
  if (range === 'all') return rows;
  if (range === 'custom') {
    const startMonth = monthKeyFromDate(startDate);
    const endMonth = monthKeyFromDate(endDate);
    return rows.filter((row) => {
      if (startMonth && row.month < startMonth) return false;
      if (endMonth && row.month > endMonth) return false;
      return true;
    });
  }
  const months = { '7d': 1, '30d': 1, '365d': 12 }[range] || rows.length;
  return rows.slice(-months);
};

// ── KPI 카드 ──
const KpiCard = ({ label, value, sub, tone }) => (
  <div className={`ir-dash-kpi ${tone ? `ir-dash-kpi-${tone}` : ''}`}>
    <div className="ir-dash-kpi-label">{label}</div>
    <div className="ir-dash-kpi-value">{value}</div>
    {sub && <div className="ir-dash-kpi-sub">{sub}</div>}
  </div>
);

// ── 월별 매출 표 ──
const MonthlyRevenueTable = React.memo(({ rows, range, startDate, endDate }) => {
  const filtered = useMemo(() => {
    return filterRowsByRange(rows, range, startDate, endDate);
  }, [endDate, rows, range, startDate]);

  if (!filtered.length) return <div className="ir-dash-empty">데이터 없음</div>;

  return (
    <div className="ir-dash-table-wrap">
      <table className="ir-dash-table">
        <thead>
          <tr>
            <th>월</th>
            <th className="ir-dash-num">B2C 토스</th>
            <th className="ir-dash-num">B2G 토스</th>
            <th className="ir-dash-num">B2G 외부판매</th>
            <th className="ir-dash-num ir-dash-th-emphasis">V3 통합 (발생주의)</th>
            <th className="ir-dash-num">회계 (현금주의)</th>
            <th className="ir-dash-num">갭</th>
            <th className="ir-dash-num">누적 회계</th>
            <th className="ir-dash-num">누적 갭</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((r) => (
            <tr key={r.month}>
              <td className="ir-dash-cell-month">{r.month}</td>
              <td className="ir-dash-num">{r.b2c_toss_value > 0 ? fmtWon(r.b2c_toss_value) : '—'}</td>
              <td className="ir-dash-num">{r.b2g_toss_value > 0 ? fmtWon(r.b2g_toss_value) : '—'}</td>
              <td className="ir-dash-num">{r.b2g_external_value > 0 ? fmtWon(r.b2g_external_value) : '—'}</td>
              <td className="ir-dash-num ir-dash-cell-emphasis">{fmtWon(r.total_value)}</td>
              <td className="ir-dash-num">{r.accounting_revenue != null ? fmtWon(r.accounting_revenue) : <span className="ir-dash-muted">—</span>}</td>
              <td className="ir-dash-num">
                {r.gap_v3_vs_accounting != null ? (
                  <span className={r.gap_v3_vs_accounting > 0 ? 'ir-dash-gap-pos' : 'ir-dash-gap-neg'}>
                    {fmtSignedWon(r.gap_v3_vs_accounting)}
                  </span>
                ) : <span className="ir-dash-muted">—</span>}
              </td>
              <td className="ir-dash-num">{r.cumulative_accounting_revenue != null ? fmtWon(r.cumulative_accounting_revenue) : <span className="ir-dash-muted">—</span>}</td>
              <td className="ir-dash-num">
                {r.cumulative_gap_v3_vs_accounting != null ? (
                  <span className={r.cumulative_gap_v3_vs_accounting > 0 ? 'ir-dash-gap-pos' : 'ir-dash-gap-neg'}>
                    {fmtSignedWon(r.cumulative_gap_v3_vs_accounting)}
                  </span>
                ) : <span className="ir-dash-muted">—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
});

const MonthlyRevenueChart = React.memo(({ rows, range, startDate, endDate }) => {
  const chartRows = useMemo(() => filterRowsByRange(rows, range, startDate, endDate), [endDate, rows, range, startDate]);
  if (!chartRows.length) return <div className="ir-dash-empty">그래프 데이터 없음</div>;
  return (
    <div className="ir-dash-chart">
      <ResponsiveContainer width="100%" height={320}>
        <LineChart data={chartRows} margin={{ top: 12, right: 18, left: 8, bottom: 8 }}>
          <CartesianGrid stroke="#F2F4F6" vertical={false} />
          <XAxis dataKey="month" tick={{ fill: '#6B7280', fontSize: 12 }} />
          <YAxis
            tick={{ fill: '#6B7280', fontSize: 12 }}
            tickFormatter={(value) => `${Math.round(value / 10000).toLocaleString('ko-KR')}만`}
            width={70}
          />
          <Tooltip
            formatter={(value, name) => [fmtWon(value), name]}
            labelFormatter={(label) => `${label}`}
          />
          <Legend />
          <Line type="monotone" dataKey="total_value" name="V3 통합" stroke="#3182F6" strokeWidth={3} dot={{ r: 3 }} />
          <Line type="monotone" dataKey="b2c_toss_value" name="B2C 토스" stroke="#22C55E" strokeWidth={2} dot={false} />
          <Line type="monotone" dataKey="b2g_toss_value" name="B2G 토스" stroke="#F59E0B" strokeWidth={2} dot={false} />
          <Line type="monotone" dataKey="b2g_external_value" name="B2G 외부" stroke="#8B5CF6" strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
});

const SaasMetricsTable = React.memo(({ headline, segments, conversion, saasMetrics, usersStats, monthlyRows }) => {
  const revenue = useMemo(() => {
    return (monthlyRows || []).reduce((acc, row) => {
      const b2g = (row.b2g_toss_value || 0) + (row.b2g_external_value || 0);
      return {
        b2g: acc.b2g + b2g,
        total: acc.total + (row.total_value || 0),
      };
    }, { b2g: 0, total: 0 });
  }, [monthlyRows]);

  const b2gRevenueShare = saasMetrics?.b2g_share_pct ?? (revenue.total ? Math.round(revenue.b2g / revenue.total * 1000) / 10 : 0);
  const b2gCustomerNumerator = saasMetrics?.b2g_customer_share_numerator ?? segments?.b2g_paying_users_unique ?? ((segments?.b2g_toss_coupon_users || 0) + (segments?.b2g_external_coupon_users || 0));
  const b2gCustomerDenominator = saasMetrics?.b2g_customer_share_denominator ?? segments?.total_paying_customers;
  const b2gCustomerShare = saasMetrics?.b2g_customer_share_pct ?? (b2gCustomerDenominator ? Math.round(b2gCustomerNumerator / b2gCustomerDenominator * 1000) / 10 : 0);

  const rows = [
    {
      metric: '누적 가입 → 결제율',
      value: fmtPct(saasMetrics?.activation_rate_pct),
      base: fmtRatio(saasMetrics?.activation_numerator ?? headline?.total_paying_customers, saasMetrics?.activation_denominator ?? usersStats?.total_users),
      formula: '고유 결제 고객 / 전체 가입자',
      source: 'users + payment_logs + 유료 쿠폰 사용',
    },
    {
      metric: '무료/마케팅 전체 → 유료 전환율',
      value: fmtPct(conversion?.marketing_to_paid_rate_pct),
      base: fmtRatio(conversion?.marketing_to_paid_count, segments?.conversion_seed_coupon_users_lifetime),
      formula: '무료/마케팅 쿠폰 사용자 전체 중 유료 채널 진입',
      source: 'user_coupons + 결제 고객 집합',
    },
    {
      metric: '체험 종료 후 유료 전환율',
      value: fmtPct(conversion?.expired_to_paid_rate_pct),
      base: fmtRatio(conversion?.expired_to_paid_count, segments?.conversion_seed_expired),
      formula: '만료된 무료/마케팅 사용자 중 유료 전환',
      source: 'benefit_end_date 기준',
    },
    {
      metric: '만료 후 미전환율',
      value: fmtPct(conversion?.expired_churn_rate_pct),
      base: fmtRatio(conversion?.expired_churn_count, segments?.conversion_seed_expired),
      formula: '만료 후 아직 결제하지 않은 사용자',
      source: '무료/마케팅 쿠폰 만료자 기준',
    },
    {
      metric: 'B2C 재구매율',
      value: fmtPct(saasMetrics?.repeat_purchase_rate_pct),
      base: fmtRatio(saasMetrics?.repeat_payers_count, saasMetrics?.repeat_purchase_denominator ?? segments?.b2c_toss_paying_users_lifetime),
      formula: '2회 이상 토스 결제 고객 / B2C 토스 결제 고객',
      source: 'payment_logs',
    },
    {
      metric: 'B2G 누적 매출 비중',
      value: fmtPct(b2gRevenueShare),
      base: `${fmtWon(revenue.b2g)} / ${fmtWon(revenue.total)}`,
      formula: 'B2G 토스 + B2G 외부판매 매출 / V3 누적 매출',
      source: '월별 V3 발생주의 매출',
    },
    {
      metric: 'B2G 결제 고객 비중',
      value: fmtPct(b2gCustomerShare),
      base: fmtRatio(b2gCustomerNumerator, b2gCustomerDenominator),
      formula: 'B2G 유료 쿠폰 사용자 / 고유 결제 고객',
      source: 'user_coupons 기준 고객 수',
    },
    {
      metric: 'ARPPU',
      value: fmtWon(headline?.ARPPU_active_won),
      base: `${fmtNum(headline?.real_paying_pro)}명 기준`,
      formula: '현재 활성 실결제 PRO의 평균 결제액',
      source: 'users.subscription 금액',
    },
  ];

  return (
    <div className="ir-dash-table-wrap">
      <table className="ir-dash-table ir-dash-metrics-table">
        <thead>
          <tr>
            <th>지표</th>
            <th className="ir-dash-num">현재값</th>
            <th>분자 / 분모</th>
            <th>산식</th>
            <th>데이터 기준</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.metric}>
              <td className="ir-dash-metric-name">{row.metric}</td>
              <td className="ir-dash-num ir-dash-cell-emphasis">{row.value}</td>
              <td>{row.base}</td>
              <td>{row.formula}</td>
              <td className="ir-dash-muted-text">{row.source}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
});

const TrialConversionSummary = React.memo(({ conversion }) => {
  const summary = conversion?.trial_conversion_summary || {};
  if (!summary.primary_label) return null;
  return (
    <div className="ir-dash-insight">
      <div>
        <div className="ir-dash-insight-label">{summary.primary_label}</div>
        <div className="ir-dash-insight-value">{fmtPct(summary.primary_rate_pct)}</div>
        <p className="ir-dash-section-note">
          {fmtRatio(summary.primary_numerator, summary.primary_denominator)} · 체험이 끝난 사용자만 분모로 봅니다.
        </p>
      </div>
      <div>
        <div className="ir-dash-insight-label">{summary.overall_label}</div>
        <div className="ir-dash-insight-value is-muted">{fmtPct(summary.overall_rate_pct)}</div>
        <p className="ir-dash-section-note">
          {fmtRatio(summary.overall_numerator, summary.overall_denominator)} · 아직 체험 중인 사용자까지 포함한 누적 관점입니다.
        </p>
      </div>
    </div>
  );
});

const SaasTrendChart = React.memo(({ rows, range, startDate, endDate }) => {
  const chartRows = useMemo(() => filterRowsByRange(rows, range, startDate, endDate), [endDate, range, rows, startDate]);
  if (!chartRows?.length) return <div className="ir-dash-empty">SaaS 추세 데이터 없음</div>;
  return (
    <>
      <p className="ir-dash-section-note" style={{ marginBottom: 8 }}>
        전환/재구매 계열만 같은 축으로 봅니다. B2G 매출 비중은 크기가 달라 아래 그래프로 분리했습니다.
      </p>
      <div className="ir-dash-chart">
        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={chartRows} margin={{ top: 12, right: 18, left: 8, bottom: 8 }}>
            <CartesianGrid stroke="#F2F4F6" vertical={false} />
            <XAxis dataKey="month" tick={{ fill: '#6B7280', fontSize: 12 }} />
            <YAxis
              domain={[0, (dataMax) => Math.max(10, Math.ceil(dataMax + 2))]}
              tick={{ fill: '#6B7280', fontSize: 12 }}
              tickFormatter={(value) => `${value}%`}
              width={54}
            />
            <Tooltip formatter={(value, name) => [`${value}%`, name]} />
            <Legend />
            <Line type="monotone" dataKey="activation_rate_pct" name="가입→결제율" stroke="#3182F6" strokeWidth={2} dot={{ r: 2 }} />
            <Line type="monotone" dataKey="expired_trial_conversion_rate_pct" name="체험 종료 후 전환율" stroke="#22C55E" strokeWidth={2} dot={{ r: 2 }} />
            <Line type="monotone" dataKey="free_to_paid_rate_pct" name="무료 전체 전환율" stroke="#8B5CF6" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="repeat_purchase_rate_pct" name="B2C 재구매율" stroke="#F59E0B" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <h3 className="ir-dash-subsection-title">B2G 매출 비중 추세</h3>
      <div className="ir-dash-chart ir-dash-chart-compact">
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={chartRows} margin={{ top: 12, right: 18, left: 8, bottom: 8 }}>
            <CartesianGrid stroke="#F2F4F6" vertical={false} />
            <XAxis dataKey="month" tick={{ fill: '#6B7280', fontSize: 12 }} />
            <YAxis domain={[0, 100]} tick={{ fill: '#6B7280', fontSize: 12 }} tickFormatter={(value) => `${value}%`} width={54} />
            <Tooltip formatter={(value, name) => [`${value}%`, name]} />
            <Legend />
            <Line type="monotone" dataKey="b2g_revenue_share_pct" name="B2G 매출 비중" stroke="#64748B" strokeWidth={2} dot={{ r: 2 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </>
  );
});

const StickinessTrendChart = React.memo(({ rows, range, startDate, endDate }) => {
  const partialRows = useMemo(() => (rows || []).filter((row) => row.is_partial_month), [rows]);
  const completedRows = useMemo(() => (rows || []).filter((row) => !row.is_partial_month), [rows]);
  const chartRows = useMemo(() => filterRowsByRange(completedRows, range, startDate, endDate), [completedRows, endDate, range, startDate]);
  const latestPartial = partialRows[partialRows.length - 1];
  if (!chartRows?.length) return <div className="ir-dash-empty">스틱니스 추세 데이터 없음</div>;
  return (
    <>
      <h3 className="ir-dash-subsection-title">스틱니스 추세</h3>
      <p className="ir-dash-section-note">
        완료된 월 기준 평균 DAU/MAU, 평균 DAU/WAU, 평균 WAU/MAU입니다. 진행 중인 달은 분모가 작아 튈 수 있어 추세선에서 제외합니다.
        {latestPartial && ` 진행 중인 ${latestPartial.month} 참고값: DAU/MAU ${fmtPct(latestPartial.avg_dau_mau_pct)}, DAU/WAU ${fmtPct(latestPartial.avg_dau_wau_pct)}, WAU/MAU ${fmtPct(latestPartial.avg_wau_mau_pct)}.`}
      </p>
      <div className="ir-dash-chart">
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartRows} margin={{ top: 12, right: 18, left: 8, bottom: 8 }}>
            <CartesianGrid stroke="#F2F4F6" vertical={false} />
            <XAxis dataKey="month" tick={{ fill: '#6B7280', fontSize: 12 }} />
            <YAxis domain={[0, (dataMax) => Math.max(50, Math.ceil(dataMax + 5))]} tick={{ fill: '#6B7280', fontSize: 12 }} tickFormatter={(value) => `${value}%`} width={54} />
            <Tooltip formatter={(value, name) => [fmtPct(value), name]} />
            <Legend />
            <ReferenceLine y={10} stroke="#94A3B8" strokeDasharray="4 4" label="DAU/MAU 10%" />
            <ReferenceLine y={25} stroke="#CBD5E1" strokeDasharray="4 4" label="DAU/MAU 25%" />
            <ReferenceLine y={40} stroke="#E2E8F0" strokeDasharray="4 4" label="WAU/MAU 40%" />
            <Line type="monotone" dataKey="avg_dau_mau_pct" name="평균 DAU/MAU" stroke="#3182F6" strokeWidth={2} dot={{ r: 2 }} />
            <Line type="monotone" dataKey="avg_dau_wau_pct" name="평균 DAU/WAU" stroke="#F59E0B" strokeWidth={2} dot={{ r: 2 }} />
            <Line type="monotone" dataKey="avg_wau_mau_pct" name="평균 WAU/MAU" stroke="#22C55E" strokeWidth={2} dot={{ r: 2 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </>
  );
});

// ── 활성 PRO ──
const ActivePROTable = React.memo(({ data }) => {
  if (!data) return null;
  const total = Math.max(1, data.total);
  const rows = [
    { label: '토스 결제 (개인 카드)', value: data.real_payment_toss_billing, tone: 'primary' },
    { label: '쿠폰 활성화 (B2G + 마케팅)', value: data.coupon_activated },
    { label: '무료 체험 (Trial)', value: data.trial },
    { label: '기타 / 레거시', value: data.unknown_legacy, muted: true },
  ];
  return (
    <table className="ir-dash-table">
      <thead><tr><th>경로</th><th className="ir-dash-num">인원</th><th className="ir-dash-num">비중</th></tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.label} className={r.muted ? 'ir-dash-row-muted' : ''}>
            <td>{r.label}</td>
            <td className={`ir-dash-num ${r.tone === 'primary' ? 'ir-dash-cell-emphasis' : ''}`}>{fmtNum(r.value)}</td>
            <td className="ir-dash-num">{(r.value / total * 100).toFixed(1)}%</td>
          </tr>
        ))}
        <tr className="ir-dash-row-total">
          <td>합계</td>
          <td className="ir-dash-num">{fmtNum(data.total)}</td>
          <td className="ir-dash-num">100.0%</td>
        </tr>
      </tbody>
    </table>
  );
});

// ── 결제 채널 ──
const PaidChannelTable = React.memo(({ data }) => {
  if (!data) return null;
  const rows = [
    { label: 'B2C 토스 (개인 카드 결제)', value: data.b2c_toss_paying_users_lifetime },
    { label: 'B2G 토스 단체구매 (카드)', value: data.b2g_toss_coupon_users },
    { label: 'B2G 외부판매 (S2B / 세금계산서)', value: data.b2g_external_coupon_users },
  ];
  const total = Math.max(1, data.paid_channel_touchpoints || rows.reduce((sum, row) => sum + (row.value || 0), 0));
  return (
    <table className="ir-dash-table">
      <thead><tr><th>채널</th><th className="ir-dash-num">채널 고객 수</th><th className="ir-dash-num">비중</th></tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.label}>
            <td>{r.label}</td>
            <td className="ir-dash-num">{fmtNum(r.value)}</td>
            <td className="ir-dash-num">{(r.value / total * 100).toFixed(1)}%</td>
          </tr>
        ))}
        <tr className="ir-dash-row-total">
          <td>채널 합계</td>
          <td className="ir-dash-num">{fmtNum(total)}</td>
          <td className="ir-dash-num">100.0%</td>
        </tr>
      </tbody>
    </table>
  );
});

// ── Forecast 시나리오 표 ──
const ForecastTable = React.memo(({ year, forecast }) => {
  if (!forecast) return null;
  const cur = forecast[`year_${year}`];
  if (!cur) return null;
  const rows = [
    { label: '보수 (Conservative)', total: cur.conservative_total, growth: forecast.applied_growth?.conservative_pct, tone: 'muted' },
    { label: '기본 (Base)', total: cur.base_total, growth: forecast.applied_growth?.base_pct, tone: 'primary' },
    { label: '낙관 (Optimistic)', total: cur.optimistic_total, growth: forecast.applied_growth?.optimistic_pct },
  ];
  return (
    <table className="ir-dash-table">
      <thead>
        <tr>
          <th>시나리오</th>
          <th className="ir-dash-num">월 평균 성장률</th>
          <th className="ir-dash-num">{year}년 추정 매출</th>
          <th className="ir-dash-num">이미 발생</th>
          <th className="ir-dash-num">잔여 (예측)</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.label} className={r.tone === 'muted' ? 'ir-dash-row-muted' : ''}>
            <td>{r.label}</td>
            <td className="ir-dash-num">{fmtPct(r.growth)}</td>
            <td className={`ir-dash-num ${r.tone === 'primary' ? 'ir-dash-cell-emphasis' : ''}`}>{fmtWon(r.total)}</td>
            <td className="ir-dash-num">{fmtWon(cur.actual_so_far)}</td>
            <td className="ir-dash-num">{fmtWon(r.total - cur.actual_so_far)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
});

const Ga4TrafficSection = React.memo(({ ga4, range, startDate, endDate }) => {
  if (!ga4) return null;
  if (!ga4.available) {
    return (
      <div className="ir-dash-section">
        <h2 className="ir-dash-section-title">GA4 웹 트래픽 / 획득</h2>
        <p className="ir-dash-section-note">비로그인 방문자를 포함한 웹 트래픽 지표입니다.</p>
        <div className="ir-dash-error" style={{ marginBottom: 0 }}>
          <p>{ga4.reason || 'GA4 데이터를 불러오지 못했습니다.'}</p>
        </div>
      </div>
    );
  }

  const trend = ga4.traffic_trend || {};
  const trendRows = (trend.rows || ga4.daily_last_90d || [])
    .map((row) => {
      const sortKey = row.yearMonth || row.date || '';
      return {
        sortKey,
        label: row.yearMonth
          ? `${row.yearMonth.slice(0, 4)}-${row.yearMonth.slice(4, 6)}`
          : row.date ? `${row.date.slice(4, 6)}/${row.date.slice(6, 8)}` : '',
        activeUsers: row.activeUsers || 0,
        sessions: row.sessions || 0,
        screenPageViews: row.screenPageViews || 0,
      };
    })
    .sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  const channelRows = (ga4.channels_last_30d || []).slice(0, 8).map((row) => ({
    channel: row.sessionDefaultChannelGroup || row.sessionSource || '(unknown)',
    sessions: row.sessions || 0,
    newUsers: row.newUsers || 0,
    engagedSessions: row.engagedSessions || 0,
  }));
  const current = ga4.core?.current || ga4.core?.last_30d || {};
  const growth = ga4.core?.growth || {};
  const stickiness = ga4.core?.stickiness || {};
  const benchmarks = ga4.core?.benchmarks || {};
  const engagementRate = Math.round((current.engagementRate || 0) * 1000) / 10;
  const bounceRate = Math.round((current.bounceRate || 0) * 1000) / 10;

  return (
    <div className="ir-dash-section">
      <h2 className="ir-dash-section-title">GA4 웹 트래픽 / 획득</h2>
      <p className="ir-dash-section-note">
        {ga4.period?.label || '선택 기간'} · 비로그인 방문자를 포함한 전체 웹 트래픽입니다. 메타 광고 유입 영향이 큰 웹 이탈률은 카드에서 제외했습니다.
        {' '}그래프 단위: {trend.granularity === 'monthly' ? '월별' : '일별'}
      </p>
      <div className="ir-dash-kpi-grid">
        <KpiCard label="활성 사용자" value={`${fmtNum(current.activeUsers)}명`} sub={`${ga4BenchmarkText.activeUsers} · 이전 기간 대비 ${growth.activeUsers_current_pct ?? '—'}%`} />
        <KpiCard label="세션" value={fmtNum(current.sessions)} sub={`${ga4BenchmarkText.sessions} · 이전 기간 대비 ${growth.sessions_current_pct ?? '—'}%`} />
        <KpiCard label="페이지뷰" value={fmtNum(current.screenPageViews)} sub={ga4BenchmarkText.pageViews} />
        <KpiCard label="참여율" value={`${engagementRate}%`} sub={ga4BenchmarkText.engagementRate} tone={engagementRate >= 50 ? 'success' : undefined} />
        <KpiCard label="DAU / MAU" value={fmtPct(stickiness.dau_mau_pct)} sub={`DAU ${fmtNum(stickiness.dau)}명 / MAU ${fmtNum(stickiness.mau)}명`} tone={stickiness.dau_mau_pct >= 10 ? 'success' : 'warning'} />
        <KpiCard label="DAU / WAU" value={fmtPct(stickiness.dau_wau_pct)} sub={`DAU ${fmtNum(stickiness.dau)}명 / WAU ${fmtNum(stickiness.wau)}명`} tone={stickiness.dau_wau_pct >= 20 ? 'success' : undefined} />
        <KpiCard label="WAU / MAU" value={fmtPct(stickiness.wau_mau_pct)} sub={`WAU ${fmtNum(stickiness.wau)}명 / MAU ${fmtNum(stickiness.mau)}명`} tone={stickiness.wau_mau_pct >= 40 ? 'success' : undefined} />
        <KpiCard label="재방문 사용자 비중" value={fmtPct(stickiness.returning_user_rate_pct)} sub={`재방문 ${fmtNum(stickiness.returning_active_users)}명 · GA4 new/returning 기준`} />
        <KpiCard label="이탈률(웹 보조)" value={`${bounceRate}%`} sub="GA4 bounceRate · 제품 이탈률과 다름" tone={bounceRate >= 60 ? 'warning' : undefined} />
      </div>
      <div className="ir-dash-table-wrap" style={{ marginBottom: 22 }}>
        <table className="ir-dash-table ir-dash-metrics-table">
          <thead>
            <tr>
              <th>GA4 지표</th>
              <th>우리 수치</th>
              <th>산업/해석 기준</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="ir-dash-metric-name">DAU/MAU</td>
              <td>{fmtPct(stickiness.dau_mau_pct)}</td>
              <td>{benchmarks.dau_mau}</td>
            </tr>
            <tr>
              <td className="ir-dash-metric-name">DAU/WAU</td>
              <td>{fmtPct(stickiness.dau_wau_pct)}</td>
              <td>{benchmarks.dau_wau}</td>
            </tr>
            <tr>
              <td className="ir-dash-metric-name">WAU/MAU</td>
              <td>{fmtPct(stickiness.wau_mau_pct)}</td>
              <td>{benchmarks.wau_mau}</td>
            </tr>
            <tr>
              <td className="ir-dash-metric-name">참여율 / 이탈률</td>
              <td>{engagementRate}% / {bounceRate}%</td>
              <td>{benchmarks.engagement_rate}</td>
            </tr>
            <tr>
              <td className="ir-dash-metric-name">리텐션 해석</td>
              <td>GA4는 웹 방문 기준</td>
              <td>{benchmarks.retention_note}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <StickinessTrendChart
        rows={ga4.stickiness_trend || []}
        range={range}
        startDate={startDate}
        endDate={endDate}
      />
      <div className="ir-dash-chart">
        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={trendRows} margin={{ top: 12, right: 18, left: 8, bottom: 8 }}>
            <CartesianGrid stroke="#F2F4F6" vertical={false} />
            <XAxis
              dataKey="label"
              interval="preserveStartEnd"
              tick={{ fill: '#6B7280', fontSize: 12 }}
              minTickGap={trend.granularity === 'monthly' ? 8 : 18}
            />
            <YAxis tick={{ fill: '#6B7280', fontSize: 12 }} />
            <Tooltip formatter={(value) => fmtNum(value)} />
            <Legend />
            <Line type="monotone" dataKey="activeUsers" name="활성 사용자" stroke="#3182F6" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="sessions" name="세션" stroke="#22C55E" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="screenPageViews" name="페이지뷰" stroke="#8B5CF6" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="ir-dash-chart">
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={channelRows} margin={{ top: 12, right: 18, left: 8, bottom: 24 }}>
            <CartesianGrid stroke="#F2F4F6" vertical={false} />
            <XAxis dataKey="channel" tick={{ fill: '#6B7280', fontSize: 12 }} />
            <YAxis tick={{ fill: '#6B7280', fontSize: 12 }} />
            <Tooltip formatter={(value) => fmtNum(value)} />
            <Legend />
            <Bar dataKey="sessions" name="세션" fill="#3182F6" radius={[6, 6, 0, 0]} />
            <Bar dataKey="newUsers" name="신규 사용자" fill="#22C55E" radius={[6, 6, 0, 0]} />
            <Bar dataKey="engagedSessions" name="참여 세션" fill="#F59E0B" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
});

const BenchmarksSection = React.memo(() => (
  <div className="ir-dash-section">
    <h2 className="ir-dash-section-title">에듀테크 SaaS 벤치마크</h2>
    <p className="ir-dash-section-note">
      외부 산업 평균은 방향성 참고용입니다. 왓퀴즈는 교사 업무 시즌성이 강하므로 제품 리텐션은 핵심 기능 완료 기준으로 별도 판단합니다.
    </p>
    <div className="ir-dash-table-wrap">
      <table className="ir-dash-table">
        <thead><tr><th>지표</th><th>산업 기준</th><th>근거</th></tr></thead>
        <tbody>
          {EDTECH_SAAS_BENCHMARKS.map((row) => (
            <tr key={row.metric}>
              <td>{row.metric}</td>
              <td>{row.benchmark}</td>
              <td>
                {row.source}
                {row.url && (
                  <>
                    {' · '}
                    <a href={row.url} target="_blank" rel="noopener noreferrer">출처</a>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
));

// ── 메인 ──
const IrDashboard = () => {
  const navigate = useNavigate();
  const [user, loading] = useAuthState(auth);
  const [data, setData] = useState(null);
  const [ga4, setGa4] = useState(null);
  const [error, setError] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [range, setRange] = useState('365d');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const fetchReport = useCallback(async (forceRefresh = false) => {
    if (!user) return;
    setFetching(true);
    setError(null);
    try {
      const token = await user.getIdToken();
      const url = forceRefresh
        ? `${getApiBaseUrl()}/api/admin/ir/dashboard/refresh`
        : `${getApiBaseUrl()}/api/admin/ir/dashboard`;
      const res = await fetch(url, {
        method: forceRefresh ? 'POST' : 'GET',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const result = await res.json();
      setData(result);
      try {
        const params = new URLSearchParams({ period: range });
        if (range === 'custom') {
          if (startDate) params.set('start_date', startDate);
          if (endDate) params.set('end_date', endDate);
        }
        const ga4Res = await fetch(`${getApiBaseUrl()}/api/admin/metrics/ga4/summary?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setGa4(await ga4Res.json());
      } catch (ga4Err) {
        setGa4({ available: false, reason: ga4Err.message || String(ga4Err) });
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setFetching(false);
    }
  }, [endDate, range, startDate, user]);

  useEffect(() => {
    if (!loading && user) fetchReport(false);
  }, [user, loading, fetchReport]);

  const downloadExcel = useCallback(async () => {
    if (!data) return;
    const XLSX = await import('xlsx');
    const wb = XLSX.utils.book_new();
    const h = data.headline || {};
    const seg = data.segments || {};
    const sm = data.saas_metrics || {};
    const fc = data.forecast || {};
    const conversion = data.conversion || {};

    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet([
      { '지표': 'V3 누적 매출 (TVR, 원)', '값': h.cumulative_v3_revenue_won },
      { '지표': `누적 회계 입금 (${h.latest_accounting_month || '최신 입력월'} 기준, 원)`, '값': h.cumulative_accounting_revenue_won },
      { '지표': '누적 갭 V3-회계 (원)', '값': h.cumulative_gap_v3_vs_accounting_won },
      { '지표': '이번 달 V3 매출 (원)', '값': h.current_month_v3_revenue_won },
      { '지표': 'MRR 추정 (원)', '값': sm.mrr_estimate_won },
      { '지표': 'ARR 추정 (원)', '값': sm.arr_estimate_won },
      { '지표': `Run Rate ARR (${sm.run_rate_basis_month || '마지막 완료월'}×12, 원)`, '값': sm.run_rate_arr_won },
      { '지표': '매출 성장 MoM (완료월 기준, %)', '값': sm.revenue_growth?.mom_pct },
      { '지표': '매출 성장 QoQ (완료월 기준, %)', '값': sm.revenue_growth?.qoq_pct },
      { '지표': '최근 6M 평균 MoM 성장 (%)', '값': sm.avg_mom_growth_recent_6m_pct },
      { '지표': 'B2G 매출 비중 (%)', '값': sm.b2g_share_pct },
      { '지표': 'B2C 매출 비중 (%)', '값': sm.b2c_share_pct },
      { '지표': '총 결제 고객', '값': h.total_paying_customers },
      { '지표': '활성 PRO 총원', '값': h.active_pro_total },
      { '지표': '누적 가입→결제율 (%)', '값': sm.activation_rate_pct },
      { '지표': 'B2C 재구매율 (%)', '값': sm.repeat_purchase_rate_pct },
      { '지표': 'ARPPU (원)', '값': h.ARPPU_active_won },
      { '지표': '무료/마케팅 쿠폰 → 유료 전환율 (%)', '값': h.marketing_to_paid_conversion_pct },
      { '지표': '무료/마케팅 쿠폰 활성 중 전환 (명)', '값': conversion.active_to_paid_count },
      { '지표': '무료/마케팅 쿠폰 만료 후 전환 (명)', '값': conversion.expired_to_paid_count },
      { '지표': '체험 종료 후 유료 전환율 (%)', '값': conversion.trial_conversion_summary?.primary_rate_pct },
      { '지표': '무료/마케팅 쿠폰 만료 후 미전환율 (%)', '값': h.expired_churn_rate_pct },
    ]), '핵심 KPI');

    if (data.monthly_integrated_revenue?.length) {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
        data.monthly_integrated_revenue.map((r) => ({
          '월': r.month,
          'B2C 토스 (원)': r.b2c_toss_value,
          'B2G 토스 (원)': r.b2g_toss_value,
          'B2G 외부판매 (원)': r.b2g_external_value,
          'V3 통합 발생주의 (원)': r.total_value,
          '회계 현금주의 (원)': r.accounting_revenue ?? '',
          '갭 V3-회계 (원)': r.gap_v3_vs_accounting ?? '',
          '누적 회계 현금주의 (원)': r.cumulative_accounting_revenue ?? '',
          '누적 갭 V3-회계 (원)': r.cumulative_gap_v3_vs_accounting ?? '',
          '누적 (원)': r.cumulative_value,
        }))
      ), '월별 매출');
    }

    if (sm.monthly_saas_metrics?.length) {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sm.monthly_saas_metrics.map((r) => ({
        '월': r.month,
        '신규 가입': r.new_users,
        '신규 결제 고객': r.new_paying_users,
        '누적 가입→결제율 (%)': r.activation_rate_pct,
        '무료 전체→유료 전환율 (%)': r.free_to_paid_rate_pct,
        '체험 종료 후 전환율 (%)': r.expired_trial_conversion_rate_pct,
        'B2C 재구매율 (%)': r.repeat_purchase_rate_pct,
        'B2G 매출 비중 (%)': r.b2g_revenue_share_pct,
      }))), 'SaaS 추세');
    }

    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet([
      { '채널': 'B2C 토스', '결제 고객': seg.b2c_toss_paying_users_lifetime },
      { '채널': 'B2G 토스 단체구매', '결제 고객': seg.b2g_toss_coupon_users },
      { '채널': 'B2G 외부판매 (S2B/세금계산서)', '결제 고객': seg.b2g_external_coupon_users },
      { '채널': '합계', '결제 고객': seg.total_paying_customers },
    ]), '결제 채널');

    if (fc.monthly_forecast?.length) {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
        fc.monthly_forecast.map((r) => ({
          '월': r.month,
          '보수 (원)': r.conservative,
          '기본 (원)': r.base,
          '낙관 (원)': r.optimistic,
        }))
      ), '미래 예측');
    }

    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet([
      { '연도': 2026, '시나리오': '보수', '추정 매출 (원)': fc.year_2026?.conservative_total },
      { '연도': 2026, '시나리오': '기본', '추정 매출 (원)': fc.year_2026?.base_total },
      { '연도': 2026, '시나리오': '낙관', '추정 매출 (원)': fc.year_2026?.optimistic_total },
      { '연도': 2027, '시나리오': '보수', '추정 매출 (원)': fc.year_2027?.conservative_total },
      { '연도': 2027, '시나리오': '기본', '추정 매출 (원)': fc.year_2027?.base_total },
      { '연도': 2027, '시나리오': '낙관', '추정 매출 (원)': fc.year_2027?.optimistic_total },
    ]), '연간 예측');

    if (ga4?.available) {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet([
        { '지표': '기간', '값': ga4.period?.label },
        { '지표': '활성 사용자', '값': ga4.core?.current?.activeUsers },
        { '지표': '세션', '값': ga4.core?.current?.sessions },
        { '지표': '페이지뷰', '값': ga4.core?.current?.screenPageViews },
        { '지표': '참여율', '값': ga4.core?.current?.engagementRate },
        { '지표': '활성 사용자 성장률', '값': ga4.core?.growth?.activeUsers_current_pct },
        { '지표': '세션 성장률', '값': ga4.core?.growth?.sessions_current_pct },
        { '지표': 'DAU/MAU (%)', '값': ga4.core?.stickiness?.dau_mau_pct },
        { '지표': 'DAU/WAU (%)', '값': ga4.core?.stickiness?.dau_wau_pct },
        { '지표': 'WAU/MAU (%)', '값': ga4.core?.stickiness?.wau_mau_pct },
        { '지표': '재방문 사용자 비중 (%)', '값': ga4.core?.stickiness?.returning_user_rate_pct },
      ]), 'GA4 요약');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(ga4.channels_last_30d || []), 'GA4 채널');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(ga4.landing_pages_last_30d || []), 'GA4 랜딩');
    }

    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(EDTECH_SAAS_BENCHMARKS),
      'SaaS 벤치마크'
    );

    XLSX.writeFile(wb, `WhatQuizAI_IR_${new Date().toISOString().slice(0, 10)}.xlsx`);
  }, [data, ga4]);

  if (loading) return <div className="ir-dash-root"><div className="ir-dash-loading"><div className="ir-dash-spinner" /><p>로딩 중...</p></div></div>;
  if (!user) { navigate('/login'); return null; }

  const h = data?.headline || {};
  const seg = data?.segments || {};
  const conv = data?.conversion || {};
  const sm = data?.saas_metrics || {};
  const fc = data?.forecast || {};
  const usersStats = data?.users_stats || {};

  // 사용자 세그먼트 비율 계산
  const totalMembers = usersStats.total_users || 0;
  const memberPct = (n) => totalMembers > 0 ? `${(n / totalMembers * 100).toFixed(1)}%` : '—';

  return (
    <div className="ir-dash-root">
      <Helmet><title>IR 대시보드 | WhatQuiz Admin</title></Helmet>

      <div className="ir-dash-header">
        <div className="ir-dash-header-text">
          <h1 className="ir-dash-title">IR 대시보드</h1>
          <p className="ir-dash-subtitle">
            팩트 기반 · 발생주의(V3) 매출 표준
            {data?.fetched_at_kst && <span className="ir-dash-meta"> · {data.fetched_at_kst}</span>}
            {data?._cache_hit && <span className="ir-dash-meta"> · 캐시 {data._cache_age_seconds}s 전</span>}
          </p>
        </div>
        <div className="ir-dash-header-actions">
          <button
            className="ir-dash-btn ir-dash-btn-ghost"
            onClick={() => fetchReport(true)}
            disabled={fetching}
          >
            {fetching ? '갱신 중...' : '최신 데이터로 갱신'}
          </button>
          {data && (
            <button className="ir-dash-btn ir-dash-btn-primary" onClick={downloadExcel}>
              엑셀 다운로드
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="ir-dash-error">
          <p>{error}</p>
          <button onClick={() => fetchReport(false)}>다시 시도</button>
        </div>
      )}
      {fetching && !data && <div className="ir-dash-loading"><div className="ir-dash-spinner" /><p>분석 중 (5~10초 소요)</p></div>}

      {data && (
        <>
          {fetching && <div className="ir-dash-overlay"><div className="ir-dash-spinner" /><p>갱신 중...</p></div>}

          {/* ── 1. Headline KPI ── */}
          <div className="ir-dash-section">
            <div className="ir-dash-headline-grid">
              <KpiCard
                label="V3 누적 매출 (TVR)"
                value={fmtWon(h.cumulative_v3_revenue_won)}
                sub="발생주의 · 쿠폰 발행 = 계약 체결 시점"
                tone="primary"
              />
              <KpiCard
                label="누적 갭 (발생 - 입금)"
                value={fmtSignedWon(h.cumulative_gap_v3_vs_accounting_won)}
                sub={`${h.latest_accounting_month || '회계 입력월'}까지 · 입금 시차 포함`}
                tone={
                  h.cumulative_gap_v3_vs_accounting_won == null
                    ? undefined
                    : h.cumulative_gap_v3_vs_accounting_won > 0 ? 'warning' : 'success'
                }
              />
              <KpiCard
                label="ARR (Run Rate)"
                value={fmtWon(sm.run_rate_arr_won)}
                sub={`${sm.run_rate_basis_month || '마지막 완료월'} 매출 × 12`}
                tone="primary"
              />
              <KpiCard
                label="누적 결제 고객"
                value={`${fmtNum(h.total_paying_customers)}명`}
                sub={`전체 회원 중 ${fmtPct(sm.activation_rate_pct)} 가입→결제`}
                tone="primary"
              />
              <KpiCard
                label="활성 PRO 사용자"
                value={`${fmtNum(h.active_pro_total)}명`}
                sub={`실 결제 ${h.real_paying_pro}명 + 쿠폰/체험`}
              />
            </div>
          </div>

          {/* ── 2. SaaS 핵심 지표 ── */}
          <div className="ir-dash-section">
            <h2 className="ir-dash-section-title">SaaS 핵심 지표</h2>
            <p className="ir-dash-section-note">
              가입, 결제 전환, 재구매, 채널 집중도를 분자와 분모까지 함께 표시합니다. 모든 수치는 production Firebase 집계 기준입니다.
            </p>
            <TrialConversionSummary conversion={conv} />
            <SaasMetricsTable
              headline={h}
              segments={seg}
              conversion={conv}
              saasMetrics={sm}
              usersStats={usersStats}
              monthlyRows={data.monthly_integrated_revenue}
            />
            <h3 className="ir-dash-subsection-title" style={{ marginTop: '20px' }}>월별 SaaS 지표 추세</h3>
            <SaasTrendChart
              rows={sm.monthly_saas_metrics || []}
              range={range}
              startDate={startDate}
              endDate={endDate}
            />
          </div>

          {/* ── 3. 월별 매출 ── */}
          <div className="ir-dash-section">
            <div className="ir-dash-section-head">
              <div>
                <h2 className="ir-dash-section-title">월별 매출</h2>
                <p className="ir-dash-section-note">
                  V3 통합 = 발생주의(쿠폰 발행 시점) · 회계 = 현금주의(세금계산서 + 입금 완료).
                  누적 갭은 회계 입력 월까지만 누적하며, 학교/S2B/계좌이체 입금 시차와 추정 단가 오차가 섞인 관리 지표입니다.
                </p>
              </div>
              <div className="ir-dash-tabs">
                {RANGES.map((r) => (
                  <button
                    key={r.key}
                    className={`ir-dash-tab ${range === r.key ? 'is-active' : ''}`}
                    onClick={() => setRange(r.key)}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
              {range === 'custom' && (
                <div className="ir-dash-date-range">
                  <input type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} />
                  <input type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} />
                </div>
              )}
            </div>
            <MonthlyRevenueChart rows={data.monthly_integrated_revenue} range={range} startDate={startDate} endDate={endDate} />
            <MonthlyRevenueTable rows={data.monthly_integrated_revenue} range={range} startDate={startDate} endDate={endDate} />
          </div>

          {/* ── 4. 미래 예측 ── */}
          <div className="ir-dash-section">
            <h2 className="ir-dash-section-title">미래 매출 예측</h2>
            <p className="ir-dash-section-note">
              {fc.assumption_note}
              <br />원시 평균 MoM 성장률 {fmtPct(fc.raw_avg_mom_growth_pct)} ·
              시작점 {fmtWon(fc.start_point_won)} (직전 3개월 평균)
            </p>
            <h3 className="ir-dash-subsection-title">2026년 추정</h3>
            <ForecastTable year={2026} forecast={fc} />
            <h3 className="ir-dash-subsection-title" style={{ marginTop: '20px' }}>2027년 추정</h3>
            <ForecastTable year={2027} forecast={fc} />
          </div>

          {/* ── 5. 결제 채널 (누적 / 매출 관점) ── */}
          <div className="ir-dash-section">
            <h2 className="ir-dash-section-title">결제 채널별 누적 고객 (매출 관점)</h2>
            <p className="ir-dash-section-note">
              <strong>채널별 고객 수 기준</strong>입니다. 위 SaaS 표의 B2G 매출 비중은 금액 기준이라 서로 다를 수 있습니다. ·
              B2G 채널 고객 비중 =
              {' '}<strong>{(((seg.b2g_toss_coupon_users || 0) + (seg.b2g_external_coupon_users || 0)) / Math.max(1, seg.paid_channel_touchpoints || seg.total_paying_customers) * 100).toFixed(1)}%</strong>
              {' '}· 고유 결제 고객 {fmtNum(seg.total_paying_customers)}명
            </p>
            <PaidChannelTable data={seg} />
          </div>

          {/* ── 6. 활성 PRO (현재 / 서비스 사용 관점) ── */}
          <div className="ir-dash-section">
            <h2 className="ir-dash-section-title">현재 활성 PRO 사용자 (서비스 관점)</h2>
            <p className="ir-dash-section-note">
              <strong>지금 이 순간 PRO 등급을 누리는 사용자</strong>만 (end_date &gt; 현재) ·
              결제·쿠폰·체험 등 활성화 경로 분포 ·
              위 결제고객과 다른 이유: <strong>만료된 결제고객은 제외</strong>되고,
              {' '}<strong>무료 쿠폰(SPRING14 등) 사용자가 추가</strong>로 포함됨
            </p>
            <ActivePROTable data={data.active_pro_breakdown} />
          </div>

          {/* ── 8. 회원 (모든 % 일관 표시) ── */}
          <div className="ir-dash-section">
            <h2 className="ir-dash-section-title">회원</h2>
            <p className="ir-dash-section-note">
              신규 가입 추이 · 월별 성장률 MoM {fmtPctSigned(sm.member_growth?.mom_pct)}
            </p>
            <div className="ir-dash-kpi-grid">
              <KpiCard
                label="전체 회원"
                value={`${fmtNum(usersStats.total_users)}명`}
                sub="모든 가입자 누적"
              />
              <KpiCard
                label="2025년 신규"
                value={`${fmtNum(usersStats.new_2025)}명`}
                sub={`전체 중 ${memberPct(usersStats.new_2025)}`}
              />
              <KpiCard
                label="2026년 신규"
                value={`${fmtNum(usersStats.new_2026)}명`}
                sub={`전체 중 ${memberPct(usersStats.new_2026)} · 진행 중`}
                tone="primary"
              />
            </div>
            <details className="ir-dash-details">
              <summary>월별 신규 가입 펼쳐 보기</summary>
              <div className="ir-dash-table-wrap">
                <table className="ir-dash-table">
                  <thead><tr><th>월</th><th className="ir-dash-num">신규 가입</th><th className="ir-dash-num">전체 중 비중</th></tr></thead>
                  <tbody>
                    {Object.entries(usersStats.new_by_month || {}).map(([m, n]) => (
                      <tr key={m}>
                        <td>{m}</td>
                        <td className="ir-dash-num">{fmtNum(n)}</td>
                        <td className="ir-dash-num">{memberPct(n)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          </div>

          <Ga4TrafficSection ga4={ga4} range={range} startDate={startDate} endDate={endDate} />
          <BenchmarksSection />

          <div className="ir-dash-footer">
            방법론은 <code>docs/IR_METHODOLOGY.md</code> · 회계 매출은 <code>backend/services/ir_service.py</code>의 <code>ACCOUNTING_REVENUE</code>에서 갱신
          </div>
        </>
      )}
    </div>
  );
};

export default IrDashboard;
